import { Loader2 } from 'lucide-react';
import type { PartnerProfileDraftType } from '@/types/types_partner';
import { partnerRequiredProfileFields } from '@/types/types_partner';
import { OnboardingStepHeader } from './OnboardingShell';
import {
  onboardingGroupHeadingClass,
  onboardingLabelClass,
  onboardingPrimaryButtonClass,
  onboardingRowClass,
  onboardingSpinnerClass,
} from './onboarding_styles';

interface OnboardingStepProfileProps {
  draft: PartnerProfileDraftType;
  onChange: (patch: Partial<PartnerProfileDraftType>) => void;
  /** Eighty Days owns these two, so they are shown rather than asked for. */
  partnerName: string;
  brand?: string;
  /** Handed the trimmed draft, which the parent's own state has yet to catch up to. */
  onContinue: (draft: PartnerProfileDraftType) => void;
  continueLabel: string;
  busy?: boolean;
}

const requiredFields = new Set<keyof PartnerProfileDraftType>(partnerRequiredProfileFields);

/**
 * Step 1 — the partner's own details: the same three blocks as the partner profile
 * page (Partner, Primary Contact, Accounting), and the same editable set, so
 * whatever a manager types here they can later change there.
 *
 * Validation is the browser's: every field the gate insists on carries
 * `required`, and the step is a real `<form>`, so submitting an incomplete one
 * raises native messages beside the offending input. That is what the mock did,
 * and it beats a bespoke error list for a form this shape.
 */
export function OnboardingStepProfile({
  draft,
  onChange,
  partnerName,
  brand,
  onContinue,
  continueLabel,
  busy,
}: OnboardingStepProfileProps) {
  // A label / input pair. Declared inside the component so it can close over
  // `draft` and `onChange`, and rendered as a call rather than as JSX — React
  // would otherwise remount the input on every keystroke and lose the caret.
  function field(
    name: keyof PartnerProfileDraftType,
    label: string,
    options: { type?: string; placeholder?: string; rows?: number; className?: string } = {},
  ) {
    const { type = 'text', placeholder, rows, className } = options;
    const required = requiredFields.has(name);
    const shared = {
      name,
      required,
      disabled: busy,
      placeholder,
      value: draft[name],
      className: 'ed-input-luxe tw:rounded-sm',
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
        onChange({ [name]: e.target.value }),
    };
    return (
      <div className={className}>
        <label htmlFor={name} className={onboardingLabelClass}>
          {label} {required && '*'}
        </label>
        {rows ? (
          <textarea id={name} rows={rows} {...shared} className={`${shared.className} tw:resize-none`} />
        ) : (
          <input id={name} type={type} {...shared} />
        )}
      </div>
    );
  }

  return (
    <form
      className='ed-step-content tw:space-y-6'
      onSubmit={(e) => {
        e.preventDefault();
        // A field holding only spaces satisfies the browser's `required` but not
        // `missingPartnerProfileFields`, which trims. Left alone, that gap is a
        // Continue button that does nothing and says nothing; trimming on the way
        // out keeps the two definitions of "filled in" identical.
        const trimmed = Object.fromEntries(
          Object.entries(draft).map(([key, value]) => [key, value.trim()]),
        ) as PartnerProfileDraftType;
        onChange(trimmed);
        onContinue(trimmed);
      }}
    >
      <OnboardingStepHeader title='Partner Information' blurb='Tell us about your organisation' />

      {/* Partner. Name and brand are read-only here for the same reason they are on
          the profile page: they identify the partner across the CMS and on every
          invitation, so changing them is Eighty Days' call. */}
      <div className={onboardingRowClass}>
        <div>
          <span className={onboardingLabelClass}>Partner Name</span>
          <p className='tw:text-white'>{partnerName}</p>
        </div>
        {brand && (
          <div>
            <span className={onboardingLabelClass}>Brand / Group</span>
            <p className='tw:text-white'>{brand}</p>
          </div>
        )}
      </div>
      {/* Plain text, not `type='url'`: that would reject a bare `example.com` for
          want of a scheme, and the admin and profile forms accept it as typed. */}
      {field('website', 'Partner Website', { placeholder: 'example.com' })}
      {field('address', 'Partner Address', { rows: 2, placeholder: 'Full address' })}

      <div className='tw:border-t tw:border-white/10 tw:pt-8'>
        <h3 className={onboardingGroupHeadingClass}>Primary Contact</h3>
        <div className='tw:space-y-6'>
          <div className={onboardingRowClass}>
            {field('contactName', 'Contact Person', { placeholder: 'Full name' })}
            {field('position', 'Position / Title', { placeholder: 'e.g., Concierge Manager' })}
          </div>
          <div className={onboardingRowClass}>
            {field('contactEmail', 'Contact Email', { type: 'email', placeholder: 'concierge@example.com' })}
            {field('phone', 'Phone Number', { type: 'tel', placeholder: '+81-3-XXXX-XXXX' })}
          </div>
        </div>
      </div>

      {/* Accounting. Absent from the mock, which only ever collected an
          application; a partner we are about to invoice needs these. */}
      <div className='tw:border-t tw:border-white/10 tw:pt-8'>
        <h3 className={onboardingGroupHeadingClass}>Accounting</h3>
        <div className='tw:space-y-6'>
          <div className={onboardingRowClass}>
            {field('accountingName', 'Accounting Contact', { placeholder: 'Name for invoicing' })}
            {field('accountingEmail', 'Accounting Email', { type: 'email', placeholder: 'accounting@example.com' })}
          </div>
          {field('billingAddress', 'Billing Address', { rows: 2, placeholder: 'Full billing address' })}
        </div>
      </div>

      <div className='tw:pt-4'>
        <button
          type='submit'
          disabled={busy}
          aria-busy={busy || undefined}
          className={onboardingPrimaryButtonClass}
        >
          {busy && <Loader2 className={onboardingSpinnerClass} />}
          {continueLabel}
        </button>
      </div>
    </form>
  );
}
